// Gear Ratio & Final Drive Ratio Calculator: field metadata, client-side per-field
// validation, ICE/EV presets, and unit conversion constants. The actual physics (tire
// geometry, per-gear table, chart curves, reverse-solve, Advanced Mode torque/force,
// Final Drive Comparison) and authoritative validation live server-side -- see
// src/lib/toolsApi.js (gearRatioToolApi) and backend/tools/gear_ratio_tool.py.
// min/max mirror the *_FIELD_SPECS in backend/core/lookup/gear_ratio_specs.py exactly.

export const KMPH_TO_MPH = 1 / 1.609344;
export const MPH_TO_KMPH = 1.609344;

export const DRIVETRAIN_TYPES = ['FWD', 'RWD', 'AWD'];
export const MIN_GEARS = 1;
export const MAX_GEARS = 10;
export const MAX_ALT_FINAL_DRIVES = 8;

export const DEFAULT_ALT_FINAL_DRIVES = [3.10, 3.50, 3.90, 4.10];

export const DEFAULT_INPUTS = {
  drivetrain: 'RWD',
  tireWidthMm: 225,
  tireAspectRatio: 45,
  wheelDiameterIn: 18,
  numGears: 6,
  maxRpm: 6500,
  maxVehicleSpeedKmph: '',
  finalDriveRatio: 3.73,
  gearRatios: [3.60, 2.19, 1.41, 1.00, 0.83, 0.67],
  referenceSpeedKmph: 100,
  advancedMode: false,
  motorTorqueNm: 350,
  driveEfficiencyPercent: 92,
  vehicleMassKg: '',
};

export const FIELD_SPECS = [
  { key: 'tireWidthMm', label: 'Tire Width', unit: 'mm', min: 100, max: 355, strictlyPositive: true },
  { key: 'tireAspectRatio', label: 'Aspect Ratio', unit: '%', min: 25, max: 85, strictlyPositive: true },
  { key: 'wheelDiameterIn', label: 'Wheel Diameter', unit: 'in', min: 10, max: 24, strictlyPositive: true },
  { key: 'maxRpm', label: 'Maximum RPM', unit: 'rpm', min: 1000, max: 20000, strictlyPositive: true },
  { key: 'finalDriveRatio', label: 'Final Drive Ratio', unit: ':1', min: 1.0, max: 12.0, strictlyPositive: true },
  { key: 'referenceSpeedKmph', label: 'Reference Speed', unit: 'km/h', min: 1, max: 400, strictlyPositive: true },
];

export const MAX_VEHICLE_SPEED_SPEC = { key: 'maxVehicleSpeedKmph', label: 'Maximum Vehicle Speed', unit: 'km/h', min: 20, max: 500, strictlyPositive: true };
export const GEAR_RATIO_FIELD_SPEC = { key: 'gearRatio', label: 'Gear Ratio', unit: ':1', min: 0.3, max: 15.0, strictlyPositive: true };

export const ADVANCED_FIELD_SPECS = [
  { key: 'motorTorqueNm', label: 'Motor/Engine Torque', unit: 'Nm', min: 1, max: 5000, strictlyPositive: true },
  { key: 'driveEfficiencyPercent', label: 'Drivetrain Efficiency', unit: '%', min: 50, max: 100, strictlyPositive: true },
];
export const VEHICLE_MASS_SPEC = { key: 'vehicleMassKg', label: 'Vehicle Mass', unit: 'kg', min: 200, max: 6000, strictlyPositive: true };

export const TARGET_FIELD_SPECS = [
  { key: 'targetSpeedKmph', label: 'Target Vehicle Speed', unit: 'km/h', min: 1, max: 500, strictlyPositive: true },
  { key: 'targetRpm', label: 'Target RPM', unit: 'rpm', min: 200, max: 20000, strictlyPositive: true },
];

export const PRESETS = {
  ice: {
    label: 'ICE Vehicle (6-Speed)',
    drivetrain: 'RWD',
    tireWidthMm: 225, tireAspectRatio: 45, wheelDiameterIn: 18,
    numGears: 6,
    gearRatios: [3.60, 2.19, 1.41, 1.00, 0.83, 0.67],
    finalDriveRatio: 3.73,
    maxRpm: 6500,
    referenceSpeedKmph: 100,
  },
  ev: {
    label: 'EV Single-Speed Reduction',
    drivetrain: 'RWD',
    tireWidthMm: 235, tireAspectRatio: 45, wheelDiameterIn: 19,
    numGears: 1,
    gearRatios: [1.00],
    finalDriveRatio: 9.0,
    maxRpm: 16000,
    referenceSpeedKmph: 100,
  },
};

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}

// Optional-field wrapper: blank is valid (means "not set"), anything else validates
// against the spec normally. Used for maxVehicleSpeedKmph and vehicleMassKg.
export function validateOptionalField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  return validateField(spec, rawValue);
}
